/**
 * TOSS Web Application - Client Route Guard
 *
 * Role-based route guard for the external client portal (decision Р31:
 * client routes gate on `UserRole.Client`, never on permission keys — the client
 * audience never enters the permission matrix at all, see
 * `permissionStore.fetchMyKeys()` and phase0-part2-client-role-and-schema.md §2.1).
 *
 * Mirrors PermissionRoute's shape: delegate the "not authenticated" case to
 * ProtectedRoute, then add the extra check this guard exists for.
 *
 * Call site: `ClientProtectedLayout` in router/layouts.tsx, which wraps every
 * authenticated `/client/*` route.
 *
 * @example
 * ```tsx
 * <ClientRoute>
 *   <ClientDashboardPage />
 * </ClientRoute>
 * ```
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  useIsAuthenticated,
  useAuthStore,
  selectIsInitializing,
  selectIsAuthPermissionsLoaded,
} from '@/store';
import { UserRole } from '@/types';
import { ROUTES } from '@/constants';
import { PageLoading } from '@/components/ui';
import { ClientOfflineScreen } from '@/components/layout/client-nav/ClientOfflineScreen';
import { ProtectedRoute } from './ProtectedRoute';

// ============================================================================
// Types
// ============================================================================

export interface ClientRouteProps {
  children: ReactNode;
  /** Where to redirect an authenticated non-client user. @default ROUTES.errors.forbidden */
  fallbackTo?: string;
}

// ============================================================================
// Component
// ============================================================================

export function ClientRoute({
  children,
  fallbackTo = ROUTES.errors.forbidden,
}: ClientRouteProps): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const isInitializing = useAuthStore(selectIsInitializing);
  const isPermissionsLoaded = useAuthStore(selectIsAuthPermissionsLoaded);
  const isOfflineBlocked = useAuthStore((s) => s.isOfflineBlocked);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (isInitializing) {
    return <PageLoading />;
  }

  if (isOfflineBlocked) {
    // The session was never disproved — the network simply was not there. Sending a customer with
    // valid tokens to a login form they cannot submit offline is how the installed app used to
    // sign people out for real. `ProtectedRoute` (the internal app) ignores this flag entirely.
    return <ClientOfflineScreen />;
  }

  if (!isAuthenticated) {
    // The portal's own login, never the internal one (decision Р16). This branch is
    // not only the "typed a /client/* URL while signed out" case — it is also every
    // session expiry: a failed token refresh dispatches `auth:logout`, which flips
    // `isAuthenticated` to false under the customer's feet. Sending them to /login
    // would drop them on the staff login form, and once the Р2 manifest ships with
    // `scope: '/client/'` it would eject them from the installed app entirely.
    return <ProtectedRoute redirectTo={ROUTES.client.login}>{children}</ProtectedRoute>;
  }

  if (!isPermissionsLoaded) {
    return <PageLoading />;
  }

  if (user?.role !== UserRole.Client) {
    return (
      <Navigate to={fallbackTo} state={{ from: location, accessDenied: true }} replace />
    );
  }

  return <>{children}</>;
}

export default ClientRoute;
